<script lang="ts">
  import {
    moveConfirmOpen,
    pendingMove,
    confirmPendingMove,
    cancelPendingMove,
  } from '../lib/stores/ui';
  import { MS_PER_DAY, getWeekStart, parseCalendarDate, formatCalendarDate } from '../lib/utils';
  import { useModalAnimation } from '../lib/useModalAnimation.svelte';

  const modal = useModalAnimation(() => $moveConfirmOpen, cancelPendingMove);

  function handleConfirm() {
    confirmPendingMove();
  }

  function handleKeydown(e: KeyboardEvent) {
    if (!$moveConfirmOpen) return;
    if (e.key === 'Escape') modal.animateClose();
    if (e.key === 'Enter') handleConfirm();
  }

  // Compute new date range for display

  function formatDate(d: Date): string {
    return formatCalendarDate(d, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function computeNewStart(move: NonNullable<typeof $pendingMove>): Date {
    if (move.moveType === 'week' && move.weekKey) {
      return getWeekStart(move.weekKey);
    } else if (move.moveType === 'month' && move.monthKey) {
      const firstOfMonth = new Date(
        Date.UTC(parseInt(move.monthKey.slice(0, 4)), parseInt(move.monthKey.slice(5, 7)) - 1, 1),
      );
      const dow = firstOfMonth.getUTCDay();
      return new Date(firstOfMonth.getTime() - dow * MS_PER_DAY);
    }
    return new Date();
  }

  let moveDescription = $derived.by(() => {
    const move = $pendingMove;
    if (!move)
      return {
        currentStart: '',
        currentEnd: '',
        newStart: '',
        newEnd: '',
        totalWeeks: 0,
        shiftWeeks: 0,
      };

    const project = move.project;
    const totalWeeks = project.totalWeeks || 1;
    const durationDays =
      totalWeeks === 1
        ? 6
        : project.startDate && project.endDate
          ? Math.round(
              (parseCalendarDate(project.endDate).getTime() -
                parseCalendarDate(project.startDate).getTime()) /
                MS_PER_DAY,
            )
          : 6;

    const newStart = computeNewStart(move);
    const newEnd = new Date(newStart.getTime() + durationDays * MS_PER_DAY);

    // Compute shift in weeks from current start
    let shiftWeeks = 0;
    if (project.startDate) {
      const currentStartMs = parseCalendarDate(project.startDate).getTime();
      shiftWeeks = Math.round((newStart.getTime() - currentStartMs) / (7 * MS_PER_DAY));
    }

    return {
      currentStart: project.startDate ? formatDate(parseCalendarDate(project.startDate)) : '',
      currentEnd: project.endDate ? formatDate(parseCalendarDate(project.endDate)) : '',
      newStart: formatDate(newStart),
      newEnd: formatDate(newEnd),
      totalWeeks,
      shiftWeeks,
    };
  });
</script>

<svelte:window onkeydown={handleKeydown} />

{#if $moveConfirmOpen && $pendingMove}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    class="modal-overlay"
    class:closing={modal.closing}
    bind:this={modal.overlayEl}
    onclick={modal.animateClose}
    role="presentation"
  >
    <div class="modal-card" onclick={(e) => e.stopPropagation()} role="dialog" tabindex="-1">
      <div class="modal-header">
        <div class="modal-icon">↗</div>
        <h2 class="modal-title">Move Multi-Week Project</h2>
      </div>

      <div class="modal-body">
        <p class="project-name">{$pendingMove.project.title}</p>
        <p class="project-duration">
          This project spans <strong>{moveDescription.totalWeeks} weeks</strong>.
          {#if moveDescription.shiftWeeks !== 0}
            Moving it will shift the entire date range
            <span class="shift-amount {moveDescription.shiftWeeks > 0 ? 'forward' : 'back'}">
              {Math.abs(moveDescription.shiftWeeks)}
              {Math.abs(moveDescription.shiftWeeks) === 1 ? 'week' : 'weeks'}
              {moveDescription.shiftWeeks > 0 ? 'forward' : 'back'}
            </span>.
          {:else}
            The date range will not change.
          {/if}
        </p>

        {#if $pendingMove.project.startDate && $pendingMove.project.endDate}
          <div class="date-change">
            <div class="date-row">
              <span class="date-label">Current</span>
              <span class="date-value">
                {moveDescription.currentStart} — {moveDescription.currentEnd}
              </span>
            </div>
            <div class="date-arrow">↓</div>
            <div class="date-row new">
              <span class="date-label">New</span>
              <span class="date-value">{moveDescription.newStart} — {moveDescription.newEnd}</span>
            </div>
          </div>
        {/if}
      </div>

      <div class="modal-actions">
        <button class="btn btn-cancel" onclick={modal.animateClose}>Cancel</button>
        <button class="btn btn-confirm" onclick={handleConfirm}>Move Project</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 60;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.5);
    animation: overlay-in 150ms ease-out both;
  }

  .modal-overlay.closing {
    animation: overlay-out 100ms ease-in both;
  }

  .modal-overlay.closing .modal-card {
    animation: card-out 100ms ease-in both;
  }

  .modal-card {
    background: white;
    border-radius: 0.75rem;
    box-shadow:
      0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 8px 10px -6px rgba(0, 0, 0, 0.1);
    width: 100%;
    max-width: 24rem;
    margin: 0 1rem;
    overflow: hidden;
    animation: card-in 150ms cubic-bezier(0.16, 1, 0.3, 1) both;
  }

  .modal-header {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 1.25rem 1.25rem 0;
  }

  .modal-icon {
    width: 2rem;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #dbeafe;
    color: #1d4ed8;
    border-radius: 0.5rem;
    font-size: 1rem;
    flex-shrink: 0;
  }

  .modal-title {
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .modal-body {
    padding: 1rem 1.25rem;
  }

  .project-name {
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 0.375rem;
  }

  .project-duration {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0 0 1rem;
    line-height: 1.4;
  }

  .shift-amount {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  .shift-amount.forward {
    color: #0369a1;
  }

  .shift-amount.back {
    color: #9333ea;
  }

  .date-change {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    padding: 0.75rem;
  }

  .date-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .date-row.new {
    color: #1d4ed8;
    font-weight: 500;
  }

  .date-label {
    font-size: 0.6875rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #9ca3af;
    width: 3.5rem;
    flex-shrink: 0;
  }

  .date-row.new .date-label {
    color: #3b82f6;
  }

  .date-value {
    font-size: 0.8125rem;
    font-family: ui-monospace, monospace;
  }

  .date-arrow {
    text-align: center;
    color: #9ca3af;
    font-size: 0.75rem;
    padding: 0.25rem 0;
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 0 1.25rem 1.25rem;
  }

  .btn {
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 150ms;
    border: none;
  }

  .btn-cancel {
    background: white;
    color: #4b5563;
    border: 1px solid #d1d5db;
  }

  .btn-cancel:hover {
    background: #f3f4f6;
  }

  .btn-confirm {
    background: #2563eb;
    color: white;
  }

  .btn-confirm:hover {
    background: #1d4ed8;
  }

  @keyframes overlay-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes overlay-out {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }

  @keyframes card-in {
    from {
      transform: scale(0.95);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes card-out {
    from {
      transform: scale(1);
      opacity: 1;
    }
    to {
      transform: scale(0.95);
      opacity: 0;
    }
  }
</style>
